'use client';

import { c as _c } from "react/compiler-runtime";
import { MonitorDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { InstallPromptRequest } from './types';
import { setInstallPromptUser, useInstallPromptStore } from './install-prompt-store';
import { BadgeVisual, ChannelVisual, NotificationVisual, TrayVisual, UnfurlVisual } from './install-prompt-visuals';
import { canShowPrompt, ensureHistory, readHistory, recordDismissed, recordShown } from './schedule';
import { useIsPrerendering } from './use-is-prerendering';
import { useDesktopOpenPromptStore } from '@/components/desktop-handoff/desktop-open-prompt-store';
import { SlackLogo } from '@/components/icons';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { Button } from '@/components/ui/button';
import { useProfile } from '@/data/profile/use-profile.client';
import { useSlackIntegration } from '@/data/slack/use-slack-integration';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { getBrowserHandoffEnvironment } from '@/lib/desktop-handoff/environment';
import { hasRecentDesktopApp } from '@/lib/desktop-handoff/open-in-app';
import { useAuth } from '@/providers/auth-provider';
type PromptContent = {
  /** The app the prompt offers. */
  app: 'desktop' | 'slack';
  title: string;
  body: string;
  visual: React.ReactNode;
};

/** Where the tray lives on this computer. */
function trayName(): string {
  return /Windows/i.test(navigator.userAgent) ? 'system tray' : 'menu bar';
}
function describePrompt(request: InstallPromptRequest): PromptContent {
  switch (request.id) {
    case 'inbox-ritual':
      return {
        app: 'desktop',
        title: 'Keep your review queue one click away',
        body: `That's your third visit to the inbox today. The cubic desktop app keeps what waits on you in your ${trayName()}.`,
        visual: <TrayVisual waiting={request.waiting} waitingCount={request.waitingCount} />
      };
    case 'second-review':
      return {
        app: 'desktop',
        title: 'Review from the notification',
        body: "That's your second review today. With the desktop app, review requests arrive as notifications you can approve from.",
        visual: <NotificationVisual title="Review requested" body={`${request.pullRequest.repo} #${request.pullRequest.number} · ${request.pullRequest.title}`} button="Approve" />
      };
    case 'inbox-zero':
      return {
        app: 'desktop',
        title: 'Inbox zero. The badge keeps it that way.',
        body: "The desktop app's badge shows the next review request, even with cubic closed.",
        visual: <BadgeVisual />
      };
    case 'copy-link':
      return {
        app: 'slack',
        title: 'Pasting this in Slack? Make it a card.',
        body: "Connect Slack and cubic links show the pull request's title and status where your team reads them.",
        visual: <UnfurlVisual pullRequest={request.pullRequest} />
      };
    case 'busy-pr':
      return {
        app: 'slack',
        title: `This PR has ${request.reviews} reviews and ${request.comments} comments. Give it a channel.`,
        body: "Connect Slack to open a channel for this pull request with everyone on it, and cubic's updates posted there.",
        visual: <ChannelVisual pullRequest={request.pullRequest} />
      };
  }
}
function PromptCard(t0) {
  const $ = _c(38);
  const {
    request,
    icon,
    action
  } = t0;
  const clear = useInstallPromptStore(_temp);
  const isShown = useInstallPromptStore(_temp2);
  const isPrerendering = useIsPrerendering();
  const isHandoffShowing = useDesktopOpenPromptStore(_temp3);
  const isHandoffDeciding = useDesktopOpenPromptStore(_temp4);
  const canShow = !isPrerendering && !isHandoffDeciding && !isHandoffShowing;
  let t1;
  if ($[0] !== request) {
    t1 = describePrompt(request);
    $[0] = request;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    title,
    body,
    visual
  } = t1;
  let t2;
  let t3;
  if ($[2] !== canShow || $[3] !== clear || $[4] !== request.id) {
    t2 = () => {
      if (!canShow || useInstallPromptStore.getState().isShown) {
        return;
      }
      const now = Date.now();
      if (!canShowPrompt(readHistory(now), request.id, now)) {
        clear();
        return;
      }
      recordShown(now);
      trackClientEvent("install_prompt_shown", undefined, {
        prompt: request.id
      });
      useInstallPromptStore.setState({
        isShown: true
      });
    };
    t3 = [canShow, clear, request.id];
    $[2] = canShow;
    $[3] = clear;
    $[4] = request.id;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== clear || $[8] !== request.id) {
    t4 = () => {
      recordDismissed(request.id);
      trackClientEvent("install_prompt_dismissed", undefined, {
        prompt: request.id
      });
      clear();
    };
    $[7] = clear;
    $[8] = request.id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const notNow = t4;
  if (!isShown || isHandoffShowing) {
    return null;
  }
  let t5;
  if ($[10] !== icon) {
    t5 = <span className="mt-0.5 shrink-0 text-primary">{icon}</span>;
    $[10] = icon;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== title) {
    t6 = <p className="text-sm font-medium text-balance">{title}</p>;
    $[12] = title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== body) {
    t7 = <p className="mt-1 text-xs text-muted-foreground">{body}</p>;
    $[14] = body;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <X className="size-3.5" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== notNow) {
    t10 = <button type="button" aria-label="Not now" onClick={notNow} className="-mr-1 -mt-1 rounded p-1 text-muted-foreground hover:text-foreground">{t9}</button>;
    $[20] = notNow;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t5 || $[24] !== t8) {
    t11 = <div className="flex items-start gap-2.5 px-1">{t5}{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t5;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== notNow) {
    t12 = <Button size="sm" variant="ghost" onClick={notNow}>Not now</Button>;
    $[26] = notNow;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== action || $[29] !== t12) {
    t13 = <div className="flex gap-2 px-1 pb-1">{action}{t12}</div>;
    $[28] = action;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11 || $[32] !== t13 || $[33] !== visual) {
    t14 = <div className="grid gap-3">{visual}{t11}{t13}</div>;
    $[31] = t11;
    $[32] = t13;
    $[33] = visual;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t14 || $[36] !== title) {
    t15 = <div role="dialog" aria-label={title} className="fixed bottom-4 left-4 z-[90] w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border bg-card p-3 shadow-xl duration-300 animate-in fade-in-0 slide-in-from-bottom-4 motion-reduce:animate-none">{t14}</div>;
    $[35] = t14;
    $[36] = title;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}
function _temp4(state_2) {
  return state_2.isDeciding;
}
function _temp3(state_1) {
  return state_1.isShowing;
}
function _temp2(state_0) {
  return state_0.isShown;
}
function _temp(state) {
  return state.clear;
}
function onAction(request: InstallPromptRequest, clear: () => void) {
  trackClientEvent('install_prompt_clicked', undefined, {
    prompt: request.id
  });
  clear();
}

/** Desktop prompts skip anyone in the app, on a phone, in an embed, or seen in the app lately. */
function DesktopPrompt(t0) {
  const $ = _c(16);
  const {
    request
  } = t0;
  const clear = useInstallPromptStore(_temp5);
  const {
    isDesktopApp
  } = useToDesktop();
  const {
    data: profile
  } = useProfile();
  const [now] = useState(_temp6);
  const [environment] = useState(getBrowserHandoffEnvironment);
  let t1;
  if ($[0] !== environment || $[1] !== isDesktopApp || $[2] !== now || $[3] !== profile) {
    t1 = isDesktopApp || environment.isMobile || environment.isIframe || (profile ? hasRecentDesktopApp(profile.desktopAppLastSeenAt, now) : false);
    $[0] = environment;
    $[1] = isDesktopApp;
    $[2] = now;
    $[3] = profile;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const hasApp = t1;
  let t2;
  let t3;
  if ($[5] !== clear || $[6] !== hasApp) {
    t2 = () => {
      if (hasApp) {
        clear();
      }
    };
    t3 = [clear, hasApp];
    $[5] = clear;
    $[6] = hasApp;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (!profile || hasApp) {
    return null;
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MonitorDown className="size-4" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== clear || $[11] !== request) {
    t5 = <Button size="sm" asChild={true}><a href="/download" target="_blank" rel="noreferrer" onClick={() => onAction(request, clear)}>Get the desktop app</a></Button>;
    $[10] = clear;
    $[11] = request;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== request || $[14] !== t5) {
    t6 = <PromptCard request={request} icon={t4} action={t5} />;
    $[13] = request;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}

/** Slack prompts only go to people without a Slack connection; the button connects it. */
function _temp6() {
  return Date.now();
}
function _temp5(state) {
  return state.clear;
}
function SlackPrompt(t0) {
  const $ = _c(12);
  const {
    request
  } = t0;
  const clear = useInstallPromptStore(_temp7);
  const {
    connectionState,
    isLoadingConnection,
    connect
  } = useSlackIntegration();
  const hasSlack = !isLoadingConnection && connectionState !== "disconnected";
  let t1;
  let t2;
  if ($[0] !== clear || $[1] !== hasSlack) {
    t1 = () => {
      if (hasSlack) {
        clear();
      }
    };
    t2 = [clear, hasSlack];
    $[0] = clear;
    $[1] = hasSlack;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (isLoadingConnection || hasSlack) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SlackLogo className="size-4" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== clear || $[6] !== connect || $[7] !== request) {
    t4 = <Button size="sm" onClick={() => {
      onAction(request, clear);
      connect();
    }}>Connect Slack</Button>;
    $[5] = clear;
    $[6] = connect;
    $[7] = request;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== request || $[10] !== t4) {
    t5 = <PromptCard request={request} icon={t3} action={t4} />;
    $[9] = request;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * Shows the install prompt a moment offered (see `offerInstallPrompt`), at most one a week. Mounted
 * once for signed-in pages.
 */
function _temp7(state) {
  return state.clear;
}
export function InstallPromptHost() {
  const $ = _c(8);
  const userId = useAuth().user?.id ?? null;
  let t0;
  if ($[0] !== userId) {
    t0 = state => state.userId === userId ? state.request : null;
    $[0] = userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const request = useInstallPromptStore(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(_temp8, t1);
  let t2;
  let t3;
  if ($[3] !== userId) {
    t2 = () => {
      if (userId) {
        setInstallPromptUser(userId);
      }
    };
    t3 = [userId];
    $[3] = userId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!request) {
    return null;
  }
  let t4;
  if ($[6] !== request) {
    t4 = describePrompt(request).app === "desktop" ? <DesktopPrompt request={request} /> : <SlackPrompt request={request} />;
    $[6] = request;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp8() {
  ensureHistory();
}